import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { admin } from '@repletoryreach/sdk-api';
import { formatRelative } from '../../lib/format';
import { useT } from '../../i18n/I18nProvider';

const POLL_MS = 60_000;

/**
 * Whether anyone is signed in as a tenant right now.
 *
 * Impersonation is the highest-risk action in the product and its audit trail,
 * `GET /v1/admin/impersonations`, was read by no screen at all: a mint was
 * fire-and-forget. A grant lives fifteen minutes, so the shell polls once a
 * minute and shows a pill for as long as one is live. Nothing renders when
 * nothing is active, and a failed poll stays silent rather than putting an
 * error in the chrome of every page.
 */
export function ImpersonationPill() {
  const t = useT();
  const { data } = useQuery({
    queryKey: ['admin', 'impersonations', 'shell'],
    queryFn: () => admin.listImpersonations(),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS
  });

  const now = Date.now();
  const active = (data?.data ?? []).filter((row) => {
    if (!row.expires_at) return false;
    const expires = new Date(row.expires_at).getTime();
    return !Number.isNaN(expires) && expires > now;
  });

  if (active.length === 0) return null;

  const first = active[0];
  const label = active.length === 1
    ? t('shell.impersonation.one', {
        tenant: first.tenant_name ?? t('shell.impersonation.unknownTenant'),
        when: formatRelative(first.expires_at as string)
      })
    : t('shell.impersonation.many', { count: active.length });

  return (
    <Tooltip label={t('shell.impersonation.tooltip')} position="bottom">
      <Link to="/audit" style={{ display: 'inline-flex' }}>
        <Badge size="xs" variant="light" color="orange">{label}</Badge>
      </Link>
    </Tooltip>
  );
}
